import type { CellValue } from 'exceljs';

export const MAX_DLP_IMPORT_BYTES = 5 * 1024 * 1024;
export class SpreadsheetReadError extends Error {
    constructor(public readonly code: 'invalidFile' | 'invalidCell' | 'unsafeId') {
        super(code);
        this.name = 'SpreadsheetReadError';
    }
}

export const readImportFile = (file: File, asText: boolean): Promise<string | ArrayBuffer> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reader.onabort = () => reject(new SpreadsheetReadError('invalidFile'));
    reader.onload = () => reader.result === null ? reject(new SpreadsheetReadError('invalidFile')) : resolve(reader.result);
    if (asText) reader.readAsText(file);
    else reader.readAsArrayBuffer(file);
});

export function excelCellText(value: CellValue, numericId = false): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') {
        if (numericId && !Number.isSafeInteger(value)) throw new SpreadsheetReadError('unsafeId');
        return String(value);
    }
    if (typeof value === 'object') {
        if ('richText' in value) return value.richText.map(part => part.text).join('');
        if ('text' in value) return value.text;
        if ('formula' in value || 'sharedFormula' in value) {
            if (value.result === undefined || value.result === null) throw new SpreadsheetReadError('invalidCell');
            return excelCellText(value.result, numericId);
        }
    }
    throw new SpreadsheetReadError('invalidCell');
}

export async function readExcelWorkbook(file: File) {
    const { Workbook } = await import('exceljs');
    const workbook = new Workbook();
    try {
        await workbook.xlsx.load(await readImportFile(file, false) as ArrayBuffer);
    } catch {
        throw new SpreadsheetReadError('invalidFile');
    }
    return workbook;
}
